import { useMutation } from "@apollo/client";
import { DragDropContext } from "@hello-pangea/dnd";
import { ReactNode } from "react";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import addTopicToMeetingCache from "@cache/add-topic-to-meeting";
import removeTopicFromMeetingCache from "@cache/remove-topic-from-meeting";

import getMeetingQuery from "../graphql/get-meeting-query";
import reorderTopicMutation from "../graphql/reorder-topic-mutation";

const DragAndDrop = ({
  children,
  meetingsGroupedById,
}: {
  children: ReactNode;
  meetingsGroupedById: {
    [key: number]: MeetingViewMeetingNodeNewPageFragmentFragment;
  };
}) => {
  const [reorderTopic] = useMutation(reorderTopicMutation, {
    update(cache, { data }) {
      const topicCacheId = cache.identify(data.reorderTopic.topic);
      if (topicCacheId) {
        removeTopicFromMeetingCache({
          topic: data.reorderTopic.topic,
          meeting: data.reorderTopic.meeting,
        });
        addTopicToMeetingCache({
          topic: data.reorderTopic.topic,
          meeting: data.reorderTopic.meeting,
          afterTopic: data.reorderTopic.afterTopic,
          beforeTopic: data.reorderTopic.beforeTopic,
        });
      }
    },
  });

  const handleDragEnd = ({
    reason,
    draggableId,
    destination,
    source,
  }: {
    reason: string;
    draggableId: string;
    destination: any;
    source: any;
  }) => {
    if (reason === "DROP" && destination) {
      // get topic and topic groups
      const sourceMeetingId = parseInt(
        source.droppableId.replace("meeting-", ""),
      );
      const topicId = parseInt(draggableId.split("-")[1]);
      const agendaTopics =
        meetingsGroupedById[sourceMeetingId]?.agendaTopics || [];
      const occurrence = agendaTopics[source.index];
      const topic = occurrence?.topic;
      const sourceMeeting = meetingsGroupedById[sourceMeetingId];

      // Same topic group
      if (source.droppableId === destination.droppableId) {
        if (source.index === destination.index) {
          return;
        }
        const pinnedCount = agendaTopics.filter(
          (agendaTopic) => agendaTopic.isPinned,
        ).length;
        if (
          (occurrence?.isPinned && destination.index >= pinnedCount) ||
          (!occurrence?.isPinned && destination.index < pinnedCount)
        )
          return;

        // reorder topic in collection
        const newAgendaTopics = Array.from(agendaTopics);
        newAgendaTopics.splice(source.index, 1);
        if (occurrence) {
          newAgendaTopics.splice(destination.index, 0, occurrence);
        }
        const newIndex = newAgendaTopics.findIndex(
          (agendaTopic) => agendaTopic.topic.id === topic?.id,
        );

        // determine the after topic and before topic based on index.
        const sectionStart = occurrence?.isPinned ? 0 : pinnedCount;
        const afterTopic =
          newIndex === sectionStart
            ? null
            : newAgendaTopics[newIndex - 1]?.topic;
        const beforeTopic =
          newIndex === sectionStart
            ? newAgendaTopics[newIndex + 1]?.topic
            : null;

        reorderTopic({
          variables: {
            afterTopicId: afterTopic?.id,
            beforeTopicId: beforeTopic?.id,
            topicId,
            meetingId: sourceMeetingId,
          },
          optimisticResponse: {
            reorderTopic: {
              meeting: {
                ...sourceMeeting,
              },
              //   topics: {
              //     edges: newMeetingEdges,
              //   },
              // },
              afterTopic,
              beforeTopic,
              topic,
              __typename: "ReorderTopicMutation",
            },
          },
          refetchQueries: [
            {
              query: getMeetingQuery,
              variables: { meetingId: sourceMeetingId },
            },
          ],
        });
      } else {
        throw new Error("Cannot move topic to another meeting.");
      }
    }
  };

  return (
    <DragDropContext nonce={window.TF?.cspNonce} onDragEnd={handleDragEnd}>
      {children}
    </DragDropContext>
  );
};

export default DragAndDrop;
